/**
 * CustomChessEngine - Flexible chess move validator for arbitrary board configurations
 * Supports variable board sizes, obstacles, and custom rules for roguelike chess
 */

import { PieceType, PieceColor, Square, UpgradeContext } from '../../types';

export interface Piece {
  type: PieceType;
  color: PieceColor;
  square: Square;
  hasMoved?: boolean;
}

export interface BoardConfig {
  validSquares: Set<string>; // Set of valid square names (e.g., "e4", "c7")
  width: number; // Board width (for bounds checking)
  height: number; // Board height (for bounds checking)
  obstacles?: Set<string>; // Blocked squares (walls, voids)
}

export interface MoveResult {
  from: Square;
  to: Square;
  captured?: PieceType;
  promotion?: PieceType;
  isLegal: boolean;
}

/**
 * Board - Manages piece positions and board state
 */
class Board {
  private pieces: Map<string, Piece> = new Map();
  public validSquares: Set<string>;
  public obstacles: Set<string>;
  public width: number;
  public height: number;
  public turn: PieceColor = 'w';

  constructor(config: BoardConfig) {
    this.validSquares = config.validSquares;
    this.obstacles = config.obstacles || new Set();
    this.width = config.width;
    this.height = config.height;
  }

  setPiece(square: Square, piece: Piece): void {
    this.pieces.set(square, piece);
  }

  getPiece(square: Square): Piece | null {
    return this.pieces.get(square) || null;
  }

  removePiece(square: Square): void {
    this.pieces.delete(square);
  }

  movePiece(from: Square, to: Square): Piece | null {
    const piece = this.getPiece(from);
    if (!piece) return null;

    const captured = this.getPiece(to);
    this.removePiece(from);
    piece.square = to;
    piece.hasMoved = true;
    this.setPiece(to, piece);

    return captured;
  }

  getAllPieces(): Piece[] {
    return Array.from(this.pieces.values());
  }

  getPiecesByColor(color: PieceColor): Piece[] {
    return this.getAllPieces().filter(p => p.color === color);
  }

  findKing(color: PieceColor): Square | null {
    const king = this.getAllPieces().find(p => p.type === 'k' && p.color === color);
    return king ? king.square : null;
  }

  findKings(color: PieceColor): Square[] {
    return this.getAllPieces()
      .filter(p => p.type === 'k' && p.color === color)
      .map(p => p.square);
  }

  isValidSquare(square: string): boolean {
    return this.validSquares.has(square) && !this.obstacles.has(square);
  }

  clone(): Board {
    const newBoard = new Board({
      validSquares: this.validSquares,
      obstacles: this.obstacles,
      width: this.width,
      height: this.height,
    });

    newBoard.turn = this.turn;

    for (const [square, piece] of this.pieces) {
      newBoard.setPiece(square as Square, { ...piece });
    }

    return newBoard;
  }
}

/**
 * MoveGenerator - Generates pseudo-legal moves (doesn't check for check)
 * Now accepts an optional UpgradeContext for upgrade-aware move generation.
 */
class MoveGenerator {
  private _pawnStartRankWhite: number | null = null;
  private _pawnStartRankBlack: number | null = null;

  constructor(private board: Board, private ctx?: UpgradeContext) {}

  /**
   * Get pawn starting rank (0-indexed) for double-move eligibility.
   */
  private getPawnStartRank(color: PieceColor): number {
    if (color === 'w' && this._pawnStartRankWhite !== null) return this._pawnStartRankWhite;
    if (color === 'b' && this._pawnStartRankBlack !== null) return this._pawnStartRankBlack;

    const ranks = new Set<number>();
    for (const sq of this.board.validSquares) {
      ranks.add(parseInt(sq[1]) - 1);
    }
    const sorted = [...ranks].sort((a, b) => a - b);

    this._pawnStartRankWhite = sorted.length >= 2 ? sorted[1] : sorted[0];
    this._pawnStartRankBlack = sorted.length >= 2 ? sorted[sorted.length - 2] : sorted[sorted.length - 1];

    return color === 'w' ? this._pawnStartRankWhite : this._pawnStartRankBlack;
  }

  private parseSquare(square: Square): [number, number] {
    const file = square.charCodeAt(0) - 97;
    const rank = parseInt(square[1]) - 1;
    return [file, rank];
  }

  private makeSquare(file: number, rank: number): string {
    if (file < 0 || file >= this.board.width || rank < 0 || rank >= this.board.height) {
      return '';
    }
    return String.fromCharCode(97 + file) + (rank + 1).toString();
  }

  private canMoveTo(square: string, pieceColor: PieceColor): boolean {
    if (!this.board.isValidSquare(square)) return false;
    const target = this.board.getPiece(square as Square);
    return !target || target.color !== pieceColor;
  }

  /** Check if upgrade is active for the player */
  private has(value: string): boolean {
    return !!this.ctx && this.ctx.values.has(value);
  }

  /** Check if a once-per-battle ability has been used */
  private isUsed(key: string): boolean {
    return !!this.ctx && this.ctx.used.has(key);
  }

  /** Check if this piece belongs to the player (upgrade recipient) */
  private isPlayer(piece: Piece): boolean {
    return !!this.ctx && piece.color === this.ctx.pieceColor;
  }

  /** Check if square is empty and valid */
  private isEmpty(square: string): boolean {
    return this.board.isValidSquare(square) && !this.board.getPiece(square as Square);
  }

  generateMoves(piece: Piece): Square[] {
    // Stunned pieces can't move
    if (this.ctx && this.ctx.stunnedSquares.has(piece.square)) {
      return [];
    }

    let moves: Square[];
    switch (piece.type) {
      case 'p':
        moves = this.generatePawnMoves(piece);
        break;
      case 'n':
        moves = this.generateKnightMoves(piece);
        break;
      case 'b':
        moves = this.generateBishopMoves(piece);
        break;
      case 'r':
        moves = this.generateRookMoves(piece);
        break;
      case 'q':
        moves = this.generateQueenMoves(piece);
        break;
      case 'k':
        moves = this.generateKingMoves(piece);
        break;
      default:
        moves = [];
    }

    // === Cross-piece upgrade moves (apply to any piece type) ===
    if (this.isPlayer(piece)) {
      // lateral_once: any piece can move ±1 file (once per piece per battle)
      if (this.has('lateral_once') && !this.isUsed(`lateral_once:${piece.square}`)) {
        const [file, rank] = this.parseSquare(piece.square);
        for (const df of [-1, 1]) {
          const sq = this.makeSquare(file + df, rank);
          if (sq && this.canMoveTo(sq, piece.color) && !moves.includes(sq as Square)) {
            moves.push(sq as Square);
          }
        }
      }

      // teleport_king: any piece can teleport adjacent to player king (once per battle)
      if (this.has('teleport_king') && !this.isUsed('teleport_king') && piece.type !== 'k') {
        const kings = this.board.findKings(piece.color);
        for (const kingSquare of kings) {
          const [kf, kr] = this.parseSquare(kingSquare);
          for (let df = -1; df <= 1; df++) {
            for (let dr = -1; dr <= 1; dr++) {
              if (df === 0 && dr === 0) continue;
              const sq = this.makeSquare(kf + df, kr + dr);
              if (sq && this.isEmpty(sq) && !moves.includes(sq as Square)) {
                moves.push(sq as Square);
              }
            }
          }
        }
      }

      // desperate_times: when player has fewer pieces, all get +1 range (1-square moves in all directions)
      if (this.has('desperate_times')) {
        const playerPieces = this.board.getPiecesByColor(piece.color);
        const enemyColor = piece.color === 'w' ? 'b' : 'w';
        const enemyPieces = this.board.getPiecesByColor(enemyColor as PieceColor);
        if (playerPieces.length < enemyPieces.length) {
          const [f, r] = this.parseSquare(piece.square);
          for (let df = -1; df <= 1; df++) {
            for (let dr = -1; dr <= 1; dr++) {
              if (df === 0 && dr === 0) continue;
              const sq = this.makeSquare(f + df, r + dr);
              if (sq && this.canMoveTo(sq, piece.color) && !moves.includes(sq as Square)) {
                moves.push(sq as Square);
              }
            }
          }
        }
      }

      // ambush: unmoved pieces can capture any enemy within 2 squares (once per battle)
      if (this.has('ambush') && !this.isUsed('ambush') && !piece.hasMoved) {
        const [f, r] = this.parseSquare(piece.square);
        for (let df = -2; df <= 2; df++) {
          for (let dr = -2; dr <= 2; dr++) {
            if (df === 0 && dr === 0) continue;
            const sq = this.makeSquare(f + df, r + dr);
            if (sq) {
              const target = this.board.getPiece(sq as Square);
              if (target && target.color !== piece.color && !moves.includes(sq as Square)) {
                moves.push(sq as Square);
              }
            }
          }
        }
      }

      // share_movement: union move patterns of adjacent friendly piece types
      if (this.has('share_movement')) {
        const [file, rank] = this.parseSquare(piece.square);
        const adjacentTypes = new Set<PieceType>();
        for (let df = -1; df <= 1; df++) {
          for (let dr = -1; dr <= 1; dr++) {
            if (df === 0 && dr === 0) continue;
            const sq = this.makeSquare(file + df, rank + dr);
            if (sq) {
              const adj = this.board.getPiece(sq as Square);
              if (adj && adj.color === piece.color && adj.type !== piece.type) {
                adjacentTypes.add(adj.type);
              }
            }
          }
        }
        // Generate moves using adjacent piece types
        for (const adjType of adjacentTypes) {
          const fakePiece: Piece = { type: adjType, color: piece.color, square: piece.square };
          // Use a fresh MoveGenerator WITHOUT upgrade context to avoid recursion
          const plainGen = new MoveGenerator(this.board);
          const extraMoves = plainGen.generateMoves(fakePiece);
          for (const m of extraMoves) {
            if (!moves.includes(m)) {
              moves.push(m);
            }
          }
        }
      }
    }

    return moves;
  }

  private generateKnightMoves(piece: Piece): Square[] {
    const [file, rank] = this.parseSquare(piece.square);
    const offsets = [
      [-2, -1], [-2, 1], [-1, -2], [-1, 2],
      [1, -2], [1, 2], [2, -1], [2, 1]
    ];

    const moves: Square[] = [];
    for (const [df, dr] of offsets) {
      const square = this.makeSquare(file + df, rank + dr);
      if (square && this.canMoveTo(square, piece.color)) {
        moves.push(square as Square);
      }
    }

    if (this.isPlayer(piece)) {
      // knight_extended: add (3,1) offsets
      if (this.has('knight_extended')) {
        const extOffsets = [
          [-3, -1], [-3, 1], [-1, -3], [-1, 3],
          [1, -3], [1, 3], [3, -1], [3, 1]
        ];
        for (const [df, dr] of extOffsets) {
          const square = this.makeSquare(file + df, rank + dr);
          if (square && this.canMoveTo(square, piece.color) && !moves.includes(square as Square)) {
            moves.push(square as Square);
          }
        }
      }

      // knight_slide: knights can stop at any square along their L-shaped path
      if (this.has('knight_slide')) {
        // Standard L-shapes: 2 in one direction, 1 in the other
        // Add intermediate squares along each L path
        const lPaths = [
          // 2 up then 1 right/left
          { steps: [[0, 1], [0, 1], [1, 0]], },
          { steps: [[0, 1], [0, 1], [-1, 0]], },
          // 2 down then 1 right/left
          { steps: [[0, -1], [0, -1], [1, 0]], },
          { steps: [[0, -1], [0, -1], [-1, 0]], },
          // 2 right then 1 up/down
          { steps: [[1, 0], [1, 0], [0, 1]], },
          { steps: [[1, 0], [1, 0], [0, -1]], },
          // 2 left then 1 up/down
          { steps: [[-1, 0], [-1, 0], [0, 1]], },
          { steps: [[-1, 0], [-1, 0], [0, -1]], },
        ];
        for (const path of lPaths) {
          let f = file, r = rank;
          for (const [df, dr] of path.steps) {
            f += df;
            r += dr;
            const sq = this.makeSquare(f, r);
            if (!sq) break;
            const pieceAt = this.board.getPiece(sq);
            if (pieceAt) {
              // Can capture enemy at this square but path is blocked after
              if (pieceAt.color !== piece.color && !moves.includes(sq as Square)) {
                moves.push(sq as Square);
              }
              break;
            }
            if (!moves.includes(sq as Square)) {
              moves.push(sq as Square);
            }
          }
        }
      }
    }

    return moves;
  }

  private generateKingMoves(piece: Piece): Square[] {
    const [file, rank] = this.parseSquare(piece.square);
    const offsets = [
      [-1, -1], [-1, 0], [-1, 1],
      [0, -1],           [0, 1],
      [1, -1],  [1, 0],  [1, 1]
    ];

    const moves: Square[] = [];
    for (const [df, dr] of offsets) {
      const square = this.makeSquare(file + df, rank + dr);
      if (square && this.canMoveTo(square, piece.color)) {
        moves.push(square as Square);
      }
    }

    if (this.isPlayer(piece)) {
      // cornered_king: king on board edge gets +1 range
      const isOnEdge = file === 0 || file === this.board.width - 1 ||
                       rank === 0 || rank === this.board.height - 1;
      if (this.has('cornered_king') && isOnEdge) {
        for (const [df, dr] of offsets) {
          const intermediate = this.makeSquare(file + df, rank + dr);
          if (!intermediate || !this.isEmpty(intermediate)) continue;
          const target = this.makeSquare(file + 2 * df, rank + 2 * dr);
          if (target && this.canMoveTo(target, piece.color) && !moves.includes(target as Square)) {
            moves.push(target as Square);
          }
        }
      }

      // king_range_1: add 2-square moves in all 8 directions
      if (this.has('king_range_1')) {
        for (const [df, dr] of offsets) {
          const intermediate = this.makeSquare(file + df, rank + dr);
          if (!intermediate || !this.isEmpty(intermediate)) continue;
          const target = this.makeSquare(file + 2 * df, rank + 2 * dr);
          if (target && this.canMoveTo(target, piece.color) && !moves.includes(target as Square)) {
            moves.push(target as Square);
          }
        }
      }

      // king_queen_once: king can move like a queen (once per battle)
      if (this.has('king_queen_once') && !this.isUsed('king_queen_once')) {
        const queenDirs = [
          [0, 1], [0, -1], [1, 0], [-1, 0],
          [1, 1], [1, -1], [-1, 1], [-1, -1]
        ];
        const queenMoves = this.generateSlidingMoves(piece, queenDirs);
        for (const m of queenMoves) {
          if (!moves.includes(m)) {
            moves.push(m);
          }
        }
      }

      // castle_moved: add castling moves ignoring "has moved" check
      if (this.has('castle_moved')) {
        this.addCastlingMoves(piece, file, rank, moves);
      }
    }

    return moves;
  }

  /** Add castling moves (flexible - ignores has-moved check) */
  private addCastlingMoves(king: Piece, kingFile: number, kingRank: number, moves: Square[]): void {
    // Find rooks of same color on same rank
    const rooks = this.board.getPiecesByColor(king.color).filter(
      p => p.type === 'r' && this.parseSquare(p.square)[1] === kingRank
    );

    for (const rook of rooks) {
      const [rookFile] = this.parseSquare(rook.square);
      const dir = rookFile > kingFile ? 1 : -1;

      // Check that all squares between king and rook are empty
      let pathClear = true;
      for (let f = kingFile + dir; f !== rookFile; f += dir) {
        const sq = this.makeSquare(f, kingRank);
        if (!sq || !this.isEmpty(sq)) {
          pathClear = false;
          break;
        }
      }

      if (pathClear) {
        // King moves 2 squares toward rook
        const destFile = kingFile + 2 * dir;
        const dest = this.makeSquare(destFile, kingRank);
        if (dest && this.board.isValidSquare(dest) && !moves.includes(dest as Square)) {
          moves.push(dest as Square);
        }
      }
    }
  }

  private generateRookMoves(piece: Piece): Square[] {
    const directions = [[0, 1], [0, -1], [1, 0], [-1, 0]];
    const moves = this.generateSlidingMoves(piece, directions);

    if (this.isPlayer(piece)) {
      // rook_diagonal_once: rook can move 1 square diagonally (once per battle)
      if (this.has('rook_diagonal_once') && !this.isUsed('rook_diagonal_once')) {
        const [rFile, rRank] = this.parseSquare(piece.square);
        const diagDirs = [[1, 1], [1, -1], [-1, 1], [-1, -1]];
        for (const [df, dr] of diagDirs) {
          const sq = this.makeSquare(rFile + df, rRank + dr);
          if (sq && this.canMoveTo(sq, piece.color) && !moves.includes(sq as Square)) {
            moves.push(sq as Square);
          }
        }
      }

      // rook_king_swap_once: rook can swap with king (once per battle)
      if (this.has('rook_king_swap_once') && !this.isUsed('rook_king_swap_once')) {
        const kings = this.board.findKings(piece.color);
        for (const kingSquare of kings) {
          if (!moves.includes(kingSquare)) {
            moves.push(kingSquare);
          }
        }
      }
    }

    return moves;
  }

  private generateBishopMoves(piece: Piece): Square[] {
    const directions = [[1, 1], [1, -1], [-1, 1], [-1, -1]];
    const moves = this.generateSlidingMoves(piece, directions);

    if (this.isPlayer(piece)) {
      // bishop_reflect: bishops bounce off board edges
      if (this.has('bishop_reflect')) {
        const [bFile, bRank] = this.parseSquare(piece.square);
        for (const [df, dr] of directions) {
          let f = bFile + df;
          let r = bRank + dr;
          let cdf = df, cdr = dr;
          let bounced = false;
          // Slide until off board, then bounce once
          while (true) {
            const sq = this.makeSquare(f, r);
            if (!sq || !this.board.isValidSquare(sq)) {
              if (bounced) break; // Only bounce once per ray
              // Determine which edge was hit and reflect
              const hitH = f < 0 || f >= this.board.width;
              const hitV = r < 0 || r >= this.board.height;
              if (hitH) { cdf = -cdf; f += cdf * 2; } // Reverse horizontal
              if (hitV) { cdr = -cdr; r += cdr * 2; } // Reverse vertical
              if (!hitH && !hitV) break;
              bounced = true;
              continue;
            }
            if (moves.includes(sq as Square)) { f += cdf; r += cdr; continue; }
            const target = this.board.getPiece(sq as Square);
            if (!target) {
              moves.push(sq as Square);
            } else {
              if (target.color !== piece.color && !moves.includes(sq as Square)) {
                moves.push(sq as Square);
              }
              break;
            }
            f += cdf;
            r += cdr;
          }
        }
      }

      // bishop_pair_jump: if 2+ player bishops, each gets one knight-jump (once per battle)
      if (this.has('bishop_pair_jump') && !this.isUsed('bishop_pair_jump')) {
        const playerBishops = this.board.getPiecesByColor(piece.color).filter(p => p.type === 'b');
        if (playerBishops.length >= 2) {
          const [bFile, bRank] = this.parseSquare(piece.square);
          const knightOffsets = [
            [-2, -1], [-2, 1], [-1, -2], [-1, 2],
            [1, -2], [1, 2], [2, -1], [2, 1]
          ];
          for (const [df, dr] of knightOffsets) {
            const sq = this.makeSquare(bFile + df, bRank + dr);
            if (sq && this.canMoveTo(sq, piece.color) && !moves.includes(sq as Square)) {
              moves.push(sq as Square);
            }
          }
        }
      }

      // color_switch_1: add 1-square orthogonal moves (once per battle)
      if (this.has('color_switch_1') && !this.isUsed('color_switch_1')) {
        const [file, rank] = this.parseSquare(piece.square);
        const orthDirs = [[0, 1], [0, -1], [1, 0], [-1, 0]];
        for (const [df, dr] of orthDirs) {
          const sq = this.makeSquare(file + df, rank + dr);
          if (sq && this.canMoveTo(sq, piece.color) && !moves.includes(sq as Square)) {
            moves.push(sq as Square);
          }
        }
      }
    }

    return moves;
  }

  private generateQueenMoves(piece: Piece): Square[] {
    const directions = [
      [0, 1], [0, -1], [1, 0], [-1, 0],
      [1, 1], [1, -1], [-1, 1], [-1, -1]
    ];
    const moves = this.generateSlidingMoves(piece, directions);

    if (this.isPlayer(piece)) {
      // queen_swap_once: queen can swap with adjacent friendly piece (once per battle)
      if (this.has('queen_swap_once') && !this.isUsed('queen_swap_once')) {
        const [qFile, qRank] = this.parseSquare(piece.square);
        const offsets = [[-1,-1],[-1,0],[-1,1],[0,-1],[0,1],[1,-1],[1,0],[1,1]];
        for (const [df, dr] of offsets) {
          const sq = this.makeSquare(qFile + df, qRank + dr);
          if (sq) {
            const target = this.board.getPiece(sq as Square);
            if (target && target.color === piece.color && target.type !== 'q' && !moves.includes(sq as Square)) {
              moves.push(sq as Square);
            }
          }
        }
      }

      // teleport_any: add all empty valid squares (once per battle)
      if (this.has('teleport_any') && !this.isUsed('teleport_any')) {
        for (const sq of this.board.validSquares) {
          if (this.board.obstacles.has(sq)) continue;
          if (this.isEmpty(sq) && !moves.includes(sq as Square)) {
            moves.push(sq as Square);
          }
        }
      }
    }

    return moves;
  }

  /**
   * Generic sliding piece move generation
   * Now handles phase_through, phase_all, phase_once upgrades
   */
  private generateSlidingMoves(piece: Piece, directions: number[][]): Square[] {
    const [file, rank] = this.parseSquare(piece.square);
    const moves: Square[] = [];

    // Determine phasing behavior
    const isPlayerPiece = this.isPlayer(piece);
    const canPhaseAll = isPlayerPiece && this.has('phase_all');
    const canPhaseBishop = isPlayerPiece && piece.type === 'b' && this.has('phase_through');
    const canPhaseOnce = isPlayerPiece && piece.type === 'b' && this.has('phase_once') && !this.isUsed('phase_once');

    for (const [df, dr] of directions) {
      let currentFile = file + df;
      let currentRank = rank + dr;
      let phasedCount = 0;

      while (true) {
        const square = this.makeSquare(currentFile, currentRank);
        if (!square || !this.board.isValidSquare(square)) break;

        const target = this.board.getPiece(square as Square);
        if (!target) {
          moves.push(square as Square);
        } else {
          if (target.color !== piece.color) {
            moves.push(square as Square); // Can capture
          }

          // Check if we can phase through
          if (canPhaseAll || canPhaseBishop) {
            // Phase through: continue past this piece
            phasedCount++;
            currentFile += df;
            currentRank += dr;
            continue;
          } else if (canPhaseOnce && phasedCount === 0) {
            // Phase once: pass through ONE piece then stop
            phasedCount++;
            currentFile += df;
            currentRank += dr;
            continue;
          }

          break; // Can't move past this piece
        }

        currentFile += df;
        currentRank += dr;
      }
    }

    return moves;
  }

  private generatePawnMoves(piece: Piece): Square[] {
    const [file, rank] = this.parseSquare(piece.square);
    const direction = piece.color === 'w' ? 1 : -1;
    const moves: Square[] = [];
    const isPlayerPiece = this.isPlayer(piece);

    // Forward move
    const forward = this.makeSquare(file, rank + direction);
    if (forward && this.board.isValidSquare(forward) && !this.board.getPiece(forward as Square)) {
      moves.push(forward as Square);

      // Double move from starting position (only if pawn hasn't moved yet)
      const startRank = this.getPawnStartRank(piece.color);
      const canDoubleOnce = isPlayerPiece && this.has('double_advance_once') && !this.isUsed('double_advance_once');

      if ((rank === startRank && !piece.hasMoved) || canDoubleOnce) {
        const doubleForward = this.makeSquare(file, rank + 2 * direction);
        if (doubleForward && this.board.isValidSquare(doubleForward as Square) &&
            !this.board.getPiece(doubleForward as Square)) {
          moves.push(doubleForward as Square);
        }
      }
    }

    // Diagonal captures
    for (const captureFile of [file - 1, file + 1]) {
      const captureSquare = this.makeSquare(captureFile, rank + direction);
      if (captureSquare && this.board.isValidSquare(captureSquare)) {
        const target = this.board.getPiece(captureSquare as Square);
        if (target && target.color !== piece.color) {
          moves.push(captureSquare as Square);
        }
      }
    }

    // === Upgrade-based pawn moves ===
    if (isPlayerPiece) {
      // backward_move: pawn can move backward 1 square (no capture)
      if (this.has('backward_move')) {
        const backward = this.makeSquare(file, rank - direction);
        if (backward && this.isEmpty(backward)) {
          moves.push(backward as Square);
        }
      }

      // lateral_capture: pawn can capture same-rank adjacent file pieces
      if (this.has('lateral_capture')) {
        for (const captureFile of [file - 1, file + 1]) {
          const lateralSquare = this.makeSquare(captureFile, rank);
          if (lateralSquare && this.board.isValidSquare(lateralSquare)) {
            const target = this.board.getPiece(lateralSquare as Square);
            if (target && target.color !== piece.color) {
              moves.push(lateralSquare as Square);
            }
          }
        }
      }

      // pawn_lateral_move: pawn can move 1 square sideways (no capture)
      if (this.has('pawn_lateral_move')) {
        for (const sideFile of [file - 1, file + 1]) {
          const sideSquare = this.makeSquare(sideFile, rank);
          if (sideSquare && this.board.isValidSquare(sideSquare) && this.isEmpty(sideSquare)) {
            if (!moves.includes(sideSquare as Square)) {
              moves.push(sideSquare as Square);
            }
          }
        }
      }
    }

    return moves;
  }
}

/**
 * CustomChessEngine - Main chess engine with legal move validation
 */
export class CustomChessEngine {
  private board: Board;
  private moveGenerator: MoveGenerator;
  private moveHistory: MoveResult[] = [];
  private upgradeContext?: UpgradeContext;
  public quiet = false;
  private multiKingMode = false;

  constructor(config: BoardConfig) {
    this.board = new Board(config);
    this.moveGenerator = new MoveGenerator(this.board, undefined);
  }

  private log(...args: unknown[]): void {
    if (!this.quiet) console.log(...args);
  }

  /** Set upgrade context for player-side move generation */
  setUpgradeContext(ctx: UpgradeContext | undefined): void {
    this.upgradeContext = ctx;
    this.moveGenerator = new MoveGenerator(this.board, ctx);
  }

  /** Enable multi-king mode (both kings must be captured to lose) */
  setMultiKingMode(enabled: boolean): void {
    this.multiKingMode = enabled;
  }

  setupPosition(pieces: Piece[], turn: PieceColor = 'w'): void {
    this.log('=== ENGINE SETUP POSITION ===');
    this.log('Setting up', pieces.length, 'pieces');
    this.log('Turn:', turn);

    this.board = new Board({
      validSquares: this.board.validSquares,
      width: this.board.width,
      height: this.board.height,
      obstacles: this.board.obstacles,
    });
    this.board.turn = turn;
    this.moveGenerator = new MoveGenerator(this.board, this.upgradeContext);

    for (const piece of pieces) {
      this.board.setPiece(piece.square, piece);
    }

    this.log('Board turn after setup:', this.board.turn);
    this.log('Pieces on board:', this.board.getAllPieces().map(p => `${p.color}${p.type}@${p.square}`).join(', '));
  }

  getPieceAt(square: Square): Piece | null {
    return this.board.getPiece(square);
  }

  getTurn(): PieceColor {
    return this.board.turn;
  }

  switchTurn(): void {
    this.board.turn = this.board.turn === 'w' ? 'b' : 'w';
  }

  removePieceAt(square: Square): void {
    this.board.removePiece(square);
  }

  getLegalMoves(square: Square): Square[] {
    this.log('=== ENGINE GET LEGAL MOVES ===');
    this.log('Square:', square);
    this.log('Current turn:', this.board.turn);

    const piece = this.board.getPiece(square);
    this.log('Piece:', piece);

    if (!piece) {
      this.log('No piece at square');
      return [];
    }

    if (piece.color !== this.board.turn) {
      this.log(`Piece color ${piece.color} does not match turn ${this.board.turn}`);
      return [];
    }

    const pseudoLegalMoves = this.moveGenerator.generateMoves(piece);
    this.log('Generated', pseudoLegalMoves.length, 'pseudo-legal moves:', pseudoLegalMoves);

    const legalMoves = pseudoLegalMoves.filter(to => {
      const isLegal = this.isMoveLegal(square, to);
      if (!isLegal) {
        this.log(`  - Move ${square} to ${to} leaves king in check`);
      }
      return isLegal;
    });

    this.log('After check filter:', legalMoves.length, 'legal moves:', legalMoves);
    return legalMoves;
  }

  private isMoveLegal(from: Square, to: Square): boolean {
    const piece = this.board.getPiece(from);
    if (!piece) return false;

    const boardCopy = this.board.clone();
    boardCopy.movePiece(from, to);

    // Check if our king(s) are in check after the move
    // Use NO upgrade context for check detection (standard rules for enemy)
    if (this.multiKingMode) {
      return !this.isAnyKingInCheck(piece.color, boardCopy);
    }
    return !this.isKingInCheck(piece.color, boardCopy);
  }

  private isKingInCheck(color: PieceColor, board: Board): boolean {
    const kingSquare = board.findKing(color);
    if (!kingSquare) return false;

    return this.isSquareAttacked(kingSquare, color, board);
  }

  /** Multi-king: check if ANY king of the given color is in check */
  private isAnyKingInCheck(color: PieceColor, board: Board): boolean {
    const kingSquares = board.findKings(color);
    if (kingSquares.length === 0) return false;

    // In multi-king mode, the position is legal as long as at least one king is safe
    // Actually: ALL kings must be safe for the position to be legal
    for (const kingSq of kingSquares) {
      if (this.isSquareAttacked(kingSq, color, board)) {
        return true;
      }
    }
    return false;
  }

  /** Check if a square is attacked by the opponent */
  private isSquareAttacked(square: Square, friendlyColor: PieceColor, board: Board): boolean {
    const opponentColor = friendlyColor === 'w' ? 'b' : 'w';
    const opponentPieces = board.getPiecesByColor(opponentColor);

    // Use a plain MoveGenerator (no upgrade context) for check detection
    const tempMoveGen = new MoveGenerator(board);
    for (const oppPiece of opponentPieces) {
      // Capture immunity: certain enemy piece types can't threaten player pieces
      if (friendlyColor === 'w' && this.upgradeContext) {
        if (oppPiece.type === 'p' && this.upgradeContext.values.has('pawn_immunity')) continue;
        if ((oppPiece.type === 'n' || oppPiece.type === 'b') && this.upgradeContext.values.has('minor_immunity')) continue;
      }

      const moves = tempMoveGen.generateMoves(oppPiece);
      if (moves.includes(square)) {
        return true;
      }
    }
    return false;
  }

  makeMove(from: Square, to: Square, promotion?: PieceType): MoveResult {
    const piece = this.board.getPiece(from);

    if (!piece || piece.color !== this.board.turn) {
      return { from, to, promotion, isLegal: false };
    }

    const pseudoLegalMoves = this.moveGenerator.generateMoves(piece);
    if (!pseudoLegalMoves.includes(to)) {
      return { from, to, promotion, isLegal: false };
    }

    if (!this.isMoveLegal(from, to)) {
      return { from, to, promotion, isLegal: false };
    }

    // Handle rook_king_swap_once: rook moving to king's square = swap
    if (piece.type === 'r' && this.upgradeContext?.values.has('rook_king_swap_once')) {
      const target = this.board.getPiece(to);
      if (target && target.type === 'k' && target.color === piece.color) {
        // Swap rook and king
        this.board.removePiece(from);
        this.board.removePiece(to);
        piece.square = to;
        target.square = from;
        this.board.setPiece(to, piece);
        this.board.setPiece(from, target);
        this.board.turn = this.board.turn === 'w' ? 'b' : 'w';
        const result: MoveResult = { from, to, isLegal: true };
        this.moveHistory.push(result);
        return result;
      }
    }

    // Handle queen_swap_once: queen moving to friendly piece = swap
    if (piece.type === 'q' && this.upgradeContext?.values.has('queen_swap_once')) {
      const target = this.board.getPiece(to);
      if (target && target.color === piece.color) {
        this.board.removePiece(from);
        this.board.removePiece(to);
        piece.square = to;
        target.square = from;
        this.board.setPiece(to, piece);
        this.board.setPiece(from, target);
        this.upgradeContext.used.add('queen_swap_once');
        this.board.turn = this.board.turn === 'w' ? 'b' : 'w';
        const result: MoveResult = { from, to, isLegal: true };
        this.moveHistory.push(result);
        return result;
      }
    }

    // Handle castle_moved: king moving 2 squares = castling
    if (piece.type === 'k' && this.upgradeContext?.values.has('castle_moved')) {
      const [fromFile, fromRank] = [from.charCodeAt(0) - 97, parseInt(from[1]) - 1];
      const [toFile] = [to.charCodeAt(0) - 97];
      const fileDiff = toFile - fromFile;
      if (Math.abs(fileDiff) === 2) {
        // Find the rook on the correct side
        const rookDir = fileDiff > 0 ? 1 : -1;
        let rookSquare: Square | null = null;
        for (let f = fromFile + rookDir; f >= 0 && f < this.board.width; f += rookDir) {
          const sq = String.fromCharCode(97 + f) + (fromRank + 1) as Square;
          const p = this.board.getPiece(sq);
          if (p && p.type === 'r' && p.color === piece.color) {
            rookSquare = sq;
            break;
          }
          if (p) break; // Blocked by another piece
        }
        if (rookSquare) {
          // Move king to destination
          this.board.movePiece(from, to);
          // Move rook to square adjacent to king (on the other side)
          const rookDest = this.makeSquare(toFile - rookDir, fromRank);
          if (rookDest) {
            this.board.movePiece(rookSquare, rookDest as Square);
          }
          this.board.turn = this.board.turn === 'w' ? 'b' : 'w';
          const result: MoveResult = { from, to, isLegal: true };
          this.moveHistory.push(result);
          return result;
        }
      }
    }

    // Mark ambush as used if an unmoved piece captures
    if (this.upgradeContext && this.upgradeContext.values.has('ambush') && !piece.hasMoved) {
      const target = this.board.getPiece(to);
      if (target && target.color !== piece.color) {
        this.upgradeContext.used.add('ambush');
      }
    }

    // Execute the move
    const captured = this.board.movePiece(from, to);

    // Mark rook_diagonal_once as used if rook made a diagonal move
    if (piece.type === 'r' && this.upgradeContext && this.upgradeContext.values.has('rook_diagonal_once')) {
      const fromFile = from.charCodeAt(0) - 97;
      const toFile = to.charCodeAt(0) - 97;
      const fromRank = parseInt(from[1]);
      const toRank = parseInt(to[1]);
      if (Math.abs(fromFile - toFile) === 1 && Math.abs(fromRank - toRank) === 1) {
        this.upgradeContext.used.add('rook_diagonal_once');
      }
    }

    // Mark double_advance_once as used if pawn moved 2 ranks
    if (piece.type === 'p' && this.upgradeContext) {
      const fromRank = parseInt(from[1]);
      const toRank = parseInt(to[1]);
      if (Math.abs(toRank - fromRank) === 2 && this.upgradeContext.values.has('double_advance_once')) {
        this.upgradeContext.used.add('double_advance_once');
      }
    }

    // Mark bishop_pair_jump as used if bishop made a knight-like move
    if (piece.type === 'b' && this.upgradeContext && this.upgradeContext.values.has('bishop_pair_jump')) {
      const fFile = from.charCodeAt(0) - 97;
      const fRank = parseInt(from[1]);
      const tFile = to.charCodeAt(0) - 97;
      const tRank = parseInt(to[1]);
      const df = Math.abs(fFile - tFile);
      const dr = Math.abs(fRank - tRank);
      if ((df === 1 && dr === 2) || (df === 2 && dr === 1)) {
        this.upgradeContext.used.add('bishop_pair_jump');
      }
    }

    // Handle pawn promotion
    if (promotion && piece.type === 'p') {
      piece.type = promotion;
      this.board.setPiece(to, piece);
    }

    // Switch turn
    this.board.turn = this.board.turn === 'w' ? 'b' : 'w';

    const result: MoveResult = {
      from,
      to,
      captured: captured?.type,
      promotion,
      isLegal: true,
    };

    this.moveHistory.push(result);
    return result;
  }

  private makeSquare(file: number, rank: number): string {
    if (file < 0 || file >= this.board.width || rank < 0 || rank >= this.board.height) {
      return '';
    }
    return String.fromCharCode(97 + file) + (rank + 1).toString();
  }

  isCheck(): boolean {
    if (this.multiKingMode) {
      return this.isAnyKingInCheck(this.board.turn, this.board);
    }
    return this.isKingInCheck(this.board.turn, this.board);
  }

  isCheckmate(): boolean {
    if (!this.isCheck()) return false;

    const pieces = this.board.getPiecesByColor(this.board.turn);
    for (const piece of pieces) {
      const legalMoves = this.getLegalMoves(piece.square);
      if (legalMoves.length > 0) {
        return false;
      }
    }

    return true;
  }

  isStalemate(): boolean {
    if (this.isCheck()) return false;

    const pieces = this.board.getPiecesByColor(this.board.turn);
    for (const piece of pieces) {
      const legalMoves = this.getLegalMoves(piece.square);
      if (legalMoves.length > 0) {
        return false;
      }
    }

    return true;
  }

  getAllPieces(): Piece[] {
    return this.board.getAllPieces();
  }

  getBoard(): (Piece | null)[][] {
    const board2D: (Piece | null)[][] = [];

    for (let rank = 7; rank >= 0; rank--) {
      board2D[7 - rank] = [];
      for (let file = 0; file < 8; file++) {
        const square = String.fromCharCode(97 + file) + (rank + 1);
        board2D[7 - rank][file] = this.board.getPiece(square as Square);
      }
    }

    return board2D;
  }

  clone(): CustomChessEngine {
    const cloned = new CustomChessEngine({
      validSquares: this.board.validSquares,
      obstacles: this.board.obstacles,
      width: this.board.width,
      height: this.board.height,
    });
    for (const piece of this.board.getAllPieces()) {
      cloned.board.setPiece(piece.square, { ...piece });
    }
    cloned.board.turn = this.board.turn;
    cloned.quiet = true;
    cloned.multiKingMode = this.multiKingMode;
    // Don't pass upgrade context to clones (used by AI search)
    return cloned;
  }
}
